import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../lib/api-client';

import { OVERRIDES_KEY } from './use-overrides';

import type {
  OverrideDualResult,
  OverrideRequestRaw,
  TemporaryFeatureFlagRaw,
} from '../types';
import type { UseMutationResult } from '@tanstack/react-query';

// Mutations de decisão da Tela 9 (dispatch por tipo — D2). Cada sucesso invalida a
// lista de overrides (todos os status) + o banner de dupla aprovação.
function useInvalidateOverrides(): () => void {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: OVERRIDES_KEY });
    void qc.invalidateQueries({ queryKey: ['dual-approval'] });
  };
}

export function useApproveOverride(): UseMutationResult<
  OverrideRequestRaw,
  Error,
  { id: string; approvalNote?: string }
> {
  const invalidate = useInvalidateOverrides();
  return useMutation({
    mutationFn: ({ id, approvalNote }) =>
      api.approveOverride(id, approvalNote !== undefined ? { approvalNote } : {}),
    onSuccess: invalidate,
  });
}

export function useRejectOverride(): UseMutationResult<
  OverrideRequestRaw,
  Error,
  { id: string; approvalNote: string }
> {
  const invalidate = useInvalidateOverrides();
  return useMutation({
    mutationFn: ({ id, approvalNote }) => api.rejectOverride(id, approvalNote),
    onSuccess: invalidate,
  });
}

export function useApproveType1(): UseMutationResult<
  { messageId: string },
  Error,
  { id: string; editedText: string; approvalNote?: string }
> {
  const invalidate = useInvalidateOverrides();
  return useMutation({
    mutationFn: ({ id, editedText, approvalNote }) =>
      api.approveOverrideType1(id, {
        editedText,
        ...(approvalNote !== undefined ? { approvalNote } : {}),
      }),
    onSuccess: invalidate,
  });
}

export function useApproveType5(): UseMutationResult<
  TemporaryFeatureFlagRaw,
  Error,
  { id: string; effectiveUntil: string; approvalNote?: string }
> {
  const invalidate = useInvalidateOverrides();
  return useMutation({
    mutationFn: ({ id, effectiveUntil, approvalNote }) =>
      api.approveOverrideType5(id, {
        effectiveUntil,
        ...(approvalNote !== undefined ? { approvalNote } : {}),
      }),
    onSuccess: invalidate,
  });
}

export function useApproveType6(): UseMutationResult<
  { exceptionId: string },
  Error,
  { id: string; documentationDocumentIds: string[]; approvalNote?: string }
> {
  const invalidate = useInvalidateOverrides();
  return useMutation({
    mutationFn: ({ id, documentationDocumentIds, approvalNote }) =>
      api.approveOverrideType6(id, {
        documentationDocumentIds,
        ...(approvalNote !== undefined ? { approvalNote } : {}),
      }),
    onSuccess: invalidate,
  });
}

export function useApproveType7(): UseMutationResult<
  OverrideDualResult,
  Error,
  { id: string; approvalNote?: string }
> {
  const invalidate = useInvalidateOverrides();
  return useMutation({
    mutationFn: ({ id, approvalNote }) =>
      api.approveOverrideType7(id, approvalNote !== undefined ? { approvalNote } : {}),
    onSuccess: invalidate,
  });
}

export function useApproveType8(): UseMutationResult<
  OverrideDualResult,
  Error,
  { id: string; documentationDocumentIds: string[]; approvalNote?: string }
> {
  const invalidate = useInvalidateOverrides();
  return useMutation({
    mutationFn: ({ id, documentationDocumentIds, approvalNote }) =>
      api.approveOverrideType8(id, {
        documentationDocumentIds,
        ...(approvalNote !== undefined ? { approvalNote } : {}),
      }),
    onSuccess: invalidate,
  });
}
